import React, { useState } from "react";
import {
  Button,
  Card,
  Table,
  Space,
  Form,
  Input,
  message,
  Popconfirm,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  AddSalesPerson_api,
  DeleteSalesPerson_api,
  FetchSalesPersons_api,
  UpdateSalesPerson_api,
} from "../../Api";
import { useAuthChecker } from "../../Context";
import { SalesPersonFormDrawer } from "./SalesPersonFormDrawer";

export const ManageSalesPersons = () => {
  const { user } = useAuthChecker();
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [form] = Form.useForm();

  // Pagination & Search State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");

  // Queries
  const {
    data: salesPersonData,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["salesPersons", page, pageSize, searchText],
    queryFn: async () => {
      try {
        const res: any = await FetchSalesPersons_api(
          page,
          pageSize,
          searchText,
        );
        if (res.isDataSet) {
          return res;
        }
        return { data: [], total: 0 };
      } catch (error) {
        console.error(error);
        message.error("Failed to fetch data");
        throw error;
      }
    },
  });

  // Handlers
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record: any) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const res: any = await DeleteSalesPerson_api(id);
      if (res.isDataDeleted) {
        message.success(res.msg);
        refetch();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      message.error("Failed to delete sales person");
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      let res: any;
      if (editingRecord) {
        res = await UpdateSalesPerson_api({
          ...values,
          id: editingRecord.id,
          userId: user?.userId,
        });
      } else {
        res = await AddSalesPerson_api({ ...values, userId: user?.userId });
      }

      if (res.isDataAdded || res.isDataUpdated) {
        message.success(res.msg);
        setModalOpen(false);
        refetch();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      console.error(error);
      message.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 60 },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number",
    },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Dealer",
      dataIndex: "dealer_name",
      key: "dealer_name",
      render: (text: string) => <Tag color="blue">{text || "N/A"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: any) => (
        <Tag color={status == 1 ? "green" : "red"}>
          {status == 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (record: any) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title={`Are you sure to delete ${record.name}?`}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Manage Sales Persons
          </h1>
          <p className="text-gray-500">Sales team management</p>
        </div>
        <div className="flex gap-4">
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            className="w-64"
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAdd}
          >
            Add New Sales Person
          </Button>
        </div>
      </div>

      <Card className="shadow-sm rounded-xl overflow-hidden border-none">
        <Table
          loading={isFetching}
          dataSource={salesPersonData?.data || []}
          className="overflow-x-scroll"
          columns={columns}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: salesPersonData?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
          }}
        />
      </Card>

      <SalesPersonFormDrawer
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        editingRecord={editingRecord}
        form={form}
        handleSubmit={handleSubmit}
        submitting={submitting}
      />
    </div>
  );
};
